import { useState, useEffect } from "react";
import { X, Check, AlertCircle, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useChat } from "@/contexts/ChatContext";
import { useToast } from "@/hooks/use-toast";

interface Change {
  field: string;
  label: string;
  before: string;
  after: string;
  status: "pending" | "approved" | "rejected";
}

export function ChatPreviewPanel() {
  const { pendingChanges, clearPendingChanges, approvePendingChanges } = useChat();
  const [changes, setChanges] = useState<Change[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  const { toast } = useToast();

  // Initialize changes from pendingChanges
  useEffect(() => {
    if (pendingChanges && pendingChanges.changes.length > 0) {
      setChanges(
        pendingChanges.changes.map((c) => ({
          ...c,
          status: "pending" as const,
        }))
      );
    } else {
      setChanges([]);
    }
  }, [pendingChanges]);

  if (!pendingChanges) return null;

  const handleApproveOne = (field: string) => {
    setChanges((prev) =>
      prev.map((c) => (c.field === field ? { ...c, status: "approved" as const } : c))
    );
  };

  const handleRejectOne = (field: string) => {
    setChanges((prev) =>
      prev.map((c) => (c.field === field ? { ...c, status: "rejected" as const } : c))
    );
  };

  const handleApproveAll = () => {
    setChanges((prev) => prev.map((c) => ({ ...c, status: "approved" as const })));
  };

  const handleClose = () => {
    setChanges([]);
    clearPendingChanges();
  };

  const handleApplyChanges = async () => {
    const approvedChanges = changes
      .filter((c) => c.status === "approved")
      .map((c) => ({ field: c.field, value: c.after }));

    if (approvedChanges.length === 0) {
      toast({
        title: "No changes approved",
        description: "Please approve at least one change to apply.",
        variant: "destructive",
      });
      return;
    }

    setIsApplying(true);
    try {
      await approvePendingChanges(approvedChanges);
      toast({
        title: "Changes applied",
        description: `${approvedChanges.length} change(s) synced to WooCommerce.`,
      });
      setChanges([]);
    } catch (error) {
      toast({
        title: "Failed to apply changes",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsApplying(false);
    }
  };

  const pendingCount = changes.filter((c) => c.status === "pending").length;
  const approvedCount = changes.filter((c) => c.status === "approved").length;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
      <div className="fixed right-0 top-0 h-full w-full max-w-3xl bg-card border-l border-border shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Preview Changes
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Review AI-suggested changes to "{pendingChanges.productName}" before applying
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-yellow-500" />
              <span className="text-muted-foreground">{pendingCount} pending</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-muted-foreground">{approvedCount} approved</span>
            </div>
          </div>
        </div>

        {/* Changes List */}
        <div className="p-4 space-y-4">
          {changes.map((change) => (
            <Card
              key={change.field}
              className={`p-4 ${
                change.status === "approved"
                  ? "border-green-500/50 bg-green-500/5"
                  : change.status === "rejected"
                  ? "border-red-500/50 bg-red-500/5"
                  : ""
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-foreground">{change.label}</h3>
                  {change.status === "approved" && (
                    <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <Check className="h-3 w-3" />
                      Approved
                    </span>
                  )}
                  {change.status === "rejected" && (
                    <span className="text-xs text-red-600 flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3 w-3" />
                      Rejected
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {change.status !== "approved" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApproveOne(change.field)}
                      className="text-green-600 hover:text-green-700"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  {change.status !== "rejected" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectOne(change.field)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  )}
                </div>
              </div>

              {/* Diff View */}
              <div className="space-y-2">
                {change.before && (
                  <div className="p-3 rounded bg-red-500/10 border border-red-500/20">
                    <div className="text-xs text-red-600 font-medium mb-1">Before</div>
                    <div className="text-sm text-foreground/80">{change.before}</div>
                  </div>
                )}
                <div className="p-3 rounded bg-green-500/10 border border-green-500/20">
                  <div className="text-xs text-green-600 font-medium mb-1">After</div>
                  <div className="text-sm text-foreground/80">{change.after}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3 justify-end">
          <Button variant="outline" onClick={handleClose} disabled={isApplying}>
            Cancel
          </Button>
          {pendingCount > 0 && (
            <Button variant="secondary" onClick={handleApproveAll} disabled={isApplying}>
              Approve All
            </Button>
          )}
          <Button onClick={handleApplyChanges} disabled={isApplying || approvedCount === 0}>
            {isApplying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Apply {approvedCount} Change{approvedCount !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
